import { addMarkers2, breakPath, mirrorX, setDefaultStyle } from "apparatus/draw";
import { P, Pivot, R, Segments } from "apparatus/library/common";
import { Group, Path, PointText, Shape } from "paper";
const allSizes = ["0.5", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "15", "20", "25", "30", "40", "50", "100"];
const BLACK = "#000000";
const BLUE = "#0423af";
const ORANGE = "#db720f";
const RED = "#aa1d1d";
const WHITE = "#eeeeee";
const GREEN = "#20b12f";
const YELLOW = "#eadb10";
const LABEL_COLOR = "#33333366";
/**
 * Original:
 *  - https://www.fishersci.com/shop/products/pyrex-reusable-class-a-volumetric-pipets-capacity-25ml-tolerance-0-03ml-color-code-blue/136511n
 *
 * Colour coding:
 *  - https://www.coleparmer.co.uk/p/blaubrand-bulb-pipettes/81461
 *  - https://1.bp.blogspot.com/-LUBA0vZZrxM/XBFI3Ogcv9I/AAAAAAAALUQ/eUV-Z2dC0DwjXWIfmB8zmRnt78P-Y0LFACLcBGAs/s1600/IMG_20181207_175733.jpg
 *
 * Example: https://www.sks-science.com/lab-supply-p-9503.html
 */
export class Pipette {
    render(appearance) {
        let radius = 5;
        let [length, bulbRadius, bulbLength, colorCode, doubleCode] = this.params(appearance.size);
        const bulbCenterRelative = 0.45;
        const bulbStart = bulbCenterRelative * length - bulbLength / 2;
        const bulbEnd = bulbStart + bulbLength;
        const radiusDiff = bulbRadius - radius;
        let oneSide = new Path(Segments([[radius, 0]], [[radius, bulbStart]], [[bulbRadius, bulbStart + bulbRadius], [0, -radiusDiff]], [[bulbRadius, bulbEnd - bulbRadius], , [0, radiusDiff]], [[radius, bulbEnd]], [[radius, length]]));
        let shape = oneSide.clone();
        mirrorX(shape);
        let liquidMask = shape.clone();
        shape.remove();
        let otherSide = oneSide.clone();
        otherSide.scale(-1, 1, P(0, 0));
        let graphic = new Group([oneSide, otherSide]);
        // Extend the lip on one side.
        oneSide.lastSegment.point.y += 5;
        setDefaultStyle(oneSide, otherSide);
        // Add marker.
        graphic.insertChild(0, new Path.Line(P(-radius, bulbStart - 20), P(radius, bulbStart - 20))
            .withStroke("thinner", LABEL_COLOR));
        // Add volume label.
        if (appearance.showLabel) {
            const fontSize = bulbRadius * 0.9;
            const label = new PointText({
                point: P(0, bulbCenterRelative * length + fontSize / 2),
                content: appearance.size,
                fillColor: LABEL_COLOR,
                fontFamily: 'sans-serif',
                fontWeight: 'bold',
                fontSize: fontSize,
                justification: "center",
            });
            graphic.addChild(label);
        }
        // Add color code.
        if (!doubleCode) {
            graphic.insertChild(0, Shape.Rectangle(R(0, 15, radius * 2, 12, Pivot.TOP_CENTER)).withFill(colorCode));
        }
        else {
            graphic.insertChildren(0, [
                Shape.Rectangle(R(0, 15, radius * 2, 5, Pivot.TOP_CENTER)).withFill(colorCode),
                Shape.Rectangle(R(0, 15 + 7, radius * 2, 5, Pivot.TOP_CENTER)).withFill(colorCode),
            ]);
        }
        // Scale everything.
        const center = graphic.bounds.center;
        graphic.scale(appearance.scale, center);
        liquidMask.scale(appearance.scale, center);
        const hitShape = graphic.bounds.expand(5).toShape();
        return {
            graphic, hitShape, liquidMask, watermark: P(5, bulbEnd),
            liquidDrainOpening: { start: liquidMask.segments[6].point, end: liquidMask.segments[5].point },
        };
    }
    params(size) {
        switch (size) {
            case "0.5":
                return [300, 7, 40, BLACK, true];
            case "1":
                return [300, 7, 40, BLUE, false];
            case "2":
                return [330, 8, 40, ORANGE, false];
            case "3":
                return [330, 9, 42, BLACK, false];
            case "4":
                return [400, 9, 42, RED, true];
            case "5":
                return [400, 10, 45, WHITE, false];
            case "6":
                return [400, 11, 45, ORANGE, true];
            case "7":
                return [400, 12, 50, GREEN, true];
            case "8":
                return [440, 13, 50, BLUE, false];
            case "9":
                return [440, 14, 50, BLACK, false];
            case "10":
                return [440, 15, 55, RED, false];
            case "15":
                return [510, 16, 55, GREEN, false];
            case "20":
                return [510, 16, 60, YELLOW, false];
            case "25":
                return [520, 17, 60, BLUE, false];
            case "30":
                return [520, 18, 65, BLACK, false];
            case "40":
                return [540, 18, 70, WHITE, false];
            case "50":
                return [540, 19, 75, RED, false];
            case "100":
                return [585, 20, 80, YELLOW, false];
        }
    }
}
Pipette.properties = {
    label: "Pipette",
    canContainLiquids: true,
    meniscus: true,
    drainable: true,
    defaultAppearance: {
        size: "10",
        scale: 0.73,
        showLabel: false,
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "scale", label: "Scale",
            min: 0.6, max: 1.0, step: 0.01, unit: "%"
        },
        {
            spec: "select", key: "size", label: "Max volume",
            options: allSizes.map(s => ({ label: s + " mL", value: s }))
        },
        { spec: "checkbox", key: "showLabel", label: "Show label", tier: "boost", allowWhenLocked: true },
    ],
    isBoostRequired: a => a.showLabel
};
export class PlasticPipette {
    render(appearance) {
        let length = appearance.length;
        let lip = 30;
        let bulb = 40;
        let bodyWidth = 8;
        let bulbWidth = 15;
        let graphic = new Path(Segments([[2, length]], [[bodyWidth / 2, length - lip]], [[bodyWidth / 2, bulb]], [[bulbWidth / 2, bulb - 10]], [[bulbWidth / 2, 0], undefined, [0, -10]]));
        mirrorX(graphic);
        let liquidMask = graphic.clone();
        setDefaultStyle(graphic);
        return { graphic: graphic, hitShape: graphic.strokeBounds.toShape(), liquidMask: liquidMask };
    }
}
PlasticPipette.properties = {
    label: "Plastic Pipette",
    canContainLiquids: true,
    defaultAppearance: {
        length: 155,
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "length", label: "Length",
            min: 100, max: 180
        }
    ],
};
export class MohrPipette {
    render(appearance) {
        let { length } = appearance;
        let shape = new Path(Segments([[4, 0]], [[4, 40]], [[8, 47], [0, -4]], [[8, length - 40], , [0, 5]], [[4, length]]));
        mirrorX(shape);
        let liquidMask = shape.clone();
        const graphic = breakPath(shape, 5);
        setDefaultStyle(graphic);
        addMarkers2(graphic, { start: P(6, length - 50), step: P(0, -4), count: (length - 150) / 4 + 1, majorEvery: 5 });
        return {
            graphic, hitShape: graphic.rectHitShape(), liquidMask,
            liquidDrainOpening: { start: P(-4, length), end: P(4, length) },
        };
    }
}
MohrPipette.properties = {
    label: "Mohr Pipette",
    canContainLiquids: true,
    meniscus: true,
    drainable: true,
    defaultAppearance: {
        length: 350
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "length", label: "Size",
            min: 250, max: 350
        }
    ],
};
